/**
 * Organization JotBill Take Config Card
 *
 * Admin editor for the organization's JotBill participation fee config
 * (jot-take PRD / GDN-1724). Rate changes apply to remittances ingested
 * after the change, never retroactively, so edits use an explicit
 * save-with-confirm flow rather than autosave.
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Percent as PercentIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { extractApiErrorMessage } from '@features/billing-readiness/utils/errorUtils';
import {
  useJotBillTakeConfigQuery,
  useUpdateJotBillTakeConfigMutation,
} from '../store/hooks';

const RATE_OPTIONS = [
  { bps: 400, label: '4%' },
  { bps: 600, label: '6%' },
  { bps: 800, label: '8%' },
];

const formatRate = (bps: number): string => `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;

interface OrganizationTakeConfigCardProps {
  organizationId: string;
}

export function OrganizationTakeConfigCard({ organizationId }: OrganizationTakeConfigCardProps) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const { data: config, isLoading } = useJotBillTakeConfigQuery({ organizationId });
  const [updateConfig, { isLoading: isSaving }] = useUpdateJotBillTakeConfigMutation();

  // Draft state, initialized from the server config
  const [enabled, setEnabled] = useState(false);
  const [takeRateBps, setTakeRateBps] = useState(400);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled);
      setTakeRateBps(config.take_rate_bps || 400);
      setEffectiveDate(config.effective_at ? config.effective_at.slice(0, 10) : '');
    }
  }, [config]);

  const isDirty =
    config !== undefined &&
    (enabled !== config.enabled ||
      (enabled && takeRateBps !== config.take_rate_bps) ||
      (enabled && effectiveDate !== (config.effective_at ? config.effective_at.slice(0, 10) : '')));

  const handleSave = async () => {
    try {
      await updateConfig({
        organizationId,
        updateJotBillTakeConfigRequest: {
          enabled,
          take_rate_bps: takeRateBps,
          effective_at: effectiveDate ? `${effectiveDate}T00:00:00+00:00` : null,
        },
      }).unwrap();
      enqueueSnackbar('JotBill take config saved', { variant: 'success' });
      setConfirmOpen(false);
    } catch (error) {
      enqueueSnackbar(extractApiErrorMessage(error, 'Failed to save JotBill take config'), {
        variant: 'error',
      });
      setConfirmOpen(false);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        height: '100%',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PercentIcon color={config?.enabled ? 'success' : 'disabled'} sx={{ mr: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              JotBill Take
            </Typography>
          </Box>
          {config?.enabled && (
            <Chip label={formatRate(config.take_rate_bps)} size="small" color="primary" />
          )}
        </Box>

        {isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={enabled}
                  onChange={e => setEnabled(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Weekly participation fee billing
                </Typography>
              }
              sx={{ mb: 1.5 }}
            />

            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
              <TextField
                select
                size="small"
                label="Rate"
                value={RATE_OPTIONS.some(o => o.bps === takeRateBps) ? takeRateBps : ''}
                onChange={e => setTakeRateBps(Number(e.target.value))}
                disabled={!enabled}
                sx={{ minWidth: 100 }}
              >
                {RATE_OPTIONS.map(option => (
                  <MenuItem key={option.bps} value={option.bps}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="date"
                label="Effective from"
                value={effectiveDate}
                onChange={e => setEffectiveDate(e.target.value)}
                disabled={!enabled}
                slotProps={{ inputLabel: { shrink: true } }}
                helperText="Remittances ingested on/after this date are billable"
              />
            </Box>

            {config?.updated_at && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Last updated {new Date(config.updated_at).toLocaleString()}
                {config.updated_by ? ` by ${config.updated_by}` : ''}
              </Typography>
            )}

            <Button
              variant="contained"
              size="small"
              disabled={!isDirty || isSaving || (enabled && !effectiveDate)}
              onClick={() => setConfirmOpen(true)}
            >
              Save
            </Button>
          </>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm take config change</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {enabled
              ? `This organization will be billed ${formatRate(takeRateBps)} of insurance collections weekly, for remittances ingested on or after ${effectiveDate || '—'}.`
              : 'Weekly participation fee billing will be disabled for this organization.'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Rate changes apply to remittances ingested after the change — never retroactively.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
